import { useRef, useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { LoadingState } from '@/components/ui/LoadingState'
import { Modal } from '@/components/ui/Modal'
import { useCampaignEstimate } from '@/hooks/useCampaignEstimate'
import { useCreateCampaign } from '@/hooks/useCreateCampaign'
import { useSendCampaign } from '@/hooks/useSendCampaign'
import { ApiError, toApiError } from '@/lib/api/errors'
import type { VenueCampaignSendResult } from '@/lib/venue/campaignTypes'
import {
  composeTargetSummaryText,
  estimateParamsReady,
  targetCountText,
  toCampaignCreateInput,
  toEstimateParams,
  type ComposeFormState,
} from '@/lib/venue/campaignViewModel'

/**
 * 발송 확인 모달(설계 §2, 요구사항 "발송 확인 모달: 대상 요약·발송 건수 재확인 후 확정. 정책 위반
 * 차단 시 거부 사유 노출").
 *
 * 🚨 mount 시 대상 산출(`useCampaignEstimate`)을 새로 조회한다 — `createQueryClient`가 `staleTime: 0`
 * 이라 같은 파라미터라도 여기서 새 쿼리 인스턴스를 마운트하면 자동으로 재확인된다(요구사항의 "재확인").
 *
 * 🚨 확정 클릭 시 작성(`POST campaigns`)→발송(`POST campaigns/{id}/send`)을 순차 호출한다. 작성 성공 후
 * 발송만 실패하면(네트워크 등) `campaignIdRef`에 남은 id로 **발송만 재호출**한다 — 재시도가 새 캠페인을
 * 만들지 않는다(설계 §자율 결정 3, #715 §D-1의 `POST send`는 `DRAFT`에 멱등하게 허용된다).
 */
export function NoticeSendConfirmModal({
  venueId,
  form,
  courts,
  onClose,
  onSent,
}: {
  venueId: string
  form: ComposeFormState
  courts: { courtId: number; name: string }[]
  onClose: () => void
  onSent: (campaignId: number) => void
}) {
  const estimateQuery = useCampaignEstimate(venueId, toEstimateParams(form), estimateParamsReady(form))
  const createMutation = useCreateCampaign(venueId)
  const sendMutation = useSendCampaign(venueId)

  const campaignIdRef = useRef<number | null>(null)
  const [sendResult, setSendResult] = useState<VenueCampaignSendResult | null>(null)
  const [generalError, setGeneralError] = useState<ApiError | null>(null)

  const pending = createMutation.isPending || sendMutation.isPending
  const retrying = campaignIdRef.current !== null

  async function handleConfirm() {
    setGeneralError(null)
    try {
      let campaignId = campaignIdRef.current
      if (campaignId === null) {
        const created = await createMutation.mutateAsync(toCampaignCreateInput(form))
        campaignId = created.summary.id
        campaignIdRef.current = campaignId
      }
      const result = await sendMutation.mutateAsync(campaignId)
      setSendResult(result)
    } catch (error) {
      setGeneralError(toApiError(error))
    }
  }

  if (sendResult?.dispatched) {
    return (
      <Modal title="발송 확인" onClose={onClose}>
        <div className="space-y-4">
          <Alert tone="info" title="발송을 시작했습니다">
            <p>발송 처리가 진행 중입니다. 잠시 후 결과가 반영됩니다.</p>
          </Alert>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                onSent(sendResult.campaignId)
              }}
            >
              확인
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  if (sendResult && !sendResult.dispatched) {
    return (
      <Modal title="발송 확인" onClose={onClose}>
        <div className="space-y-4">
          <Alert tone="danger" title="정책 위반으로 발송이 차단됐습니다">
            {/* 🚨 `blockReasonMessage` 필드는 없다(#732 §데이터 모델 교정) — `blockReasonLabel`이 정본 문구다. */}
            <p>{sendResult.blockReasonLabel ?? '발송 정책을 확인해 주세요.'}</p>
          </Alert>
          <div className="flex justify-end">
            <Button type="button" variant="secondary" onClick={onClose}>
              닫기
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  const estimate = estimateQuery.data
  const targetCount = estimate?.targetCount ?? null
  const sendDisabled = pending || estimateQuery.isLoading || targetCount === null || estimate?.estimateAvailable === false

  return (
    <Modal title="발송 확인" onClose={onClose}>
      <div className="space-y-4">
        <Alert tone="danger">
          <p>발송 후에는 내용을 수정하거나 취소할 수 없습니다.</p>
        </Alert>

        {estimateQuery.isLoading ? <LoadingState rows={2} label="대상 수를 확인하는 중" /> : null}

        {estimateQuery.isError ? (
          <Alert tone="danger">
            <p>{toApiError(estimateQuery.error).message}</p>
          </Alert>
        ) : null}

        {estimate ? (
          <div className="space-y-1 rounded-lg bg-slateish-50 px-3 py-2 text-sm text-ink-700">
            <p>대상: {composeTargetSummaryText(form, courts)}</p>
            <p>발송 건수: {targetCountText(targetCount)}</p>
            {!estimate.estimateAvailable ? (
              <p className="text-xs text-danger-500">{estimate.unavailableMessage ?? '대상을 산출할 수 없습니다.'}</p>
            ) : null}
          </div>
        ) : null}

        {generalError ? (
          <Alert tone="danger">
            <p>{generalError.message}</p>
          </Alert>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            취소
          </Button>
          <Button
            type="button"
            disabled={sendDisabled}
            onClick={() => {
              void handleConfirm()
            }}
          >
            {pending ? '처리 중…' : retrying ? '다시 발송' : '확정'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
